import {
  FileText,
  StickyNote,
  Scale,
  Mail,
  Users,
  Route,
  Search as SearchIcon,
  type LucideIcon,
} from 'lucide-react';
import {
  DOCUMENT_DESTINATION_ORDER,
  DOCUMENT_DESTINATION_PRESENTATION,
} from '@hector/shared-types';

/**
 * Single source of truth for document category metadata across legalworkspace.
 *
 * Each category groups every label / icon / color / PDF style used by the UI.
 * Consumers must NEVER redefine these mappings locally — derive from this
 * module via the helpers below. This prevents the kind of drift we hit in
 * 2026-04 where the Timeline filter still showed "Projet" months after the
 * category was renamed to "Mes écritures" everywhere else.
 *
 * Database keys (`procedure`, `nos_pieces`, ...) are immutable contracts with
 * the backend. Only the UI labels are owned here.
 */

export type DocumentCategoryKey =
  | 'nos_pieces'
  | 'notes_projets'
  | 'procedure'
  | 'correspondances'
  | 'partie_adverse'
  | 'recherche'
  | 'procedures';

export type DocumentSubcategoryKey =
  | 'pieces_communiquees'
  | 'actes_communiques'
  // Natures de la catégorie « recherche » (TICKET-278) — déposées par l'avocat
  // et traitées DIFFÉREMMENT par le pipeline JP : une décision brute est citée
  // en jurisprudence (provenance), une doctrine/analyse oriente la génération
  // d'arguments (planner) et fait découvrir des décisions citées.
  | 'decision'
  | 'doctrine'
  | 'analyse';

export interface DocumentCategoryConfig {
  key: DocumentCategoryKey;
  label: string;
  description: string;
  icon: LucideIcon;
  /** Tailwind text color class (used by Timeline badges) */
  color: string;
  /** Tailwind background + border class (used by Timeline badges) */
  bgColor: string;
  /** Hex colors for @react-pdf rendering (Timeline PDF export) */
  pdf: {
    bg: string;
    text: string;
    border: string;
  };
  /** Whether this category exposes subcategories in the UI */
  hasSubcategories: boolean;
}

export interface DocumentSubcategoryConfig {
  key: DocumentSubcategoryKey;
  label: string;
  parentCategory: DocumentCategoryKey;
}

// Neutral grey palette is intentional: the Timeline relies on the colored
// "tier" badges to convey semantics, the source-type badge stays muted to
// avoid visual competition.
const NEUTRAL_TEXT_COLOR = 'text-grey-800';
const NEUTRAL_BG_COLOR = 'bg-grey-50 border-grey-200';
const NEUTRAL_PDF = {
  bg: '#f9fafb',
  text: '#374151',
  border: '#e5e7eb',
} as const;

export const DOCUMENT_CATEGORIES: Record<DocumentCategoryKey, DocumentCategoryConfig> = {
  nos_pieces: {
    key: 'nos_pieces',
    label: DOCUMENT_DESTINATION_PRESENTATION.nos_pieces.label,
    description: 'Pièces du dossier',
    icon: FileText,
    color: NEUTRAL_TEXT_COLOR,
    bgColor: NEUTRAL_BG_COLOR,
    pdf: NEUTRAL_PDF,
    hasSubcategories: false,
  },
  notes_projets: {
    key: 'notes_projets',
    label: DOCUMENT_DESTINATION_PRESENTATION.notes_projets.label,
    description: 'Notes internes et projets d\'actes',
    icon: StickyNote,
    color: NEUTRAL_TEXT_COLOR,
    bgColor: NEUTRAL_BG_COLOR,
    pdf: NEUTRAL_PDF,
    hasSubcategories: false,
  },
  procedure: {
    key: 'procedure',
    label: DOCUMENT_DESTINATION_PRESENTATION.procedure.label,
    description: 'Mes écritures (conclusions, assignations, etc.)',
    icon: Scale,
    color: NEUTRAL_TEXT_COLOR,
    bgColor: NEUTRAL_BG_COLOR,
    pdf: NEUTRAL_PDF,
    hasSubcategories: false,
  },
  correspondances: {
    key: 'correspondances',
    label: 'Correspondances',
    description: 'Correspondances',
    icon: Mail,
    color: NEUTRAL_TEXT_COLOR,
    bgColor: NEUTRAL_BG_COLOR,
    pdf: NEUTRAL_PDF,
    hasSubcategories: false,
  },
  partie_adverse: {
    key: 'partie_adverse',
    label: DOCUMENT_DESTINATION_PRESENTATION.partie_adverse.label,
    description: 'Documents partie adverse',
    icon: Users,
    color: NEUTRAL_TEXT_COLOR,
    bgColor: NEUTRAL_BG_COLOR,
    pdf: NEUTRAL_PDF,
    hasSubcategories: true,
  },
  recherche: {
    key: 'recherche',
    // Search icon is the long-standing icon used in DossierView's sidebar
    // and MoveToDialog. The Timeline previously used BookOpen — we standardize
    // on Search since the sidebar is the most-visible surface.
    label: DOCUMENT_DESTINATION_PRESENTATION.recherche.label,
    description: 'Recherches juridiques',
    icon: SearchIcon,
    color: NEUTRAL_TEXT_COLOR,
    bgColor: NEUTRAL_BG_COLOR,
    pdf: NEUTRAL_PDF,
    // TICKET-278 — la « nature » d'un document recherche (décision / doctrine /
    // analyse) est un ATTRIBUT par document capturé au dépôt (sélecteur dans les
    // dialogs), PAS une scission de dossier comme `partie_adverse`. On laisse donc
    // `hasSubcategories: false` : le mécanisme de sous-catégories (arbre de
    // dossiers scopé, MoveToDialog) reste réservé à `partie_adverse`. Les natures
    // vivent dans `DOCUMENT_SUBCATEGORIES` + `RECHERCHE_NATURES` pour les libellés
    // et le sélecteur, sans déclencher cette machinerie.
    hasSubcategories: false,
  },
  // « Juridiction et greffe » — bac de documents à part entière (folders/upload/delete
  // comme les autres catégories), pour ce qui vient de la juridiction et du
  // greffe. Clé DB `procedures` : la colonne `case_documents.document_category`
  // est du TEXT libre côté backend, donc aucune contrainte serveur à lever pour
  // la gestion documentaire. (Interaction Case Wiki hors périmètre — cf. hub.)
  procedures: {
    key: 'procedures',
    label: DOCUMENT_DESTINATION_PRESENTATION.procedures.label,
    description: 'Décisions, déclaration d’appel, mise en état, convocations et avis du greffe',
    icon: Route,
    color: NEUTRAL_TEXT_COLOR,
    bgColor: NEUTRAL_BG_COLOR,
    pdf: NEUTRAL_PDF,
    hasSubcategories: false,
  },
};

export const DOCUMENT_SUBCATEGORIES: Record<DocumentSubcategoryKey, DocumentSubcategoryConfig> = {
  pieces_communiquees: {
    key: 'pieces_communiquees',
    label: 'Pièces adverses',
    parentCategory: 'partie_adverse',
  },
  actes_communiques: {
    key: 'actes_communiques',
    label: 'Écritures adverses',
    parentCategory: 'partie_adverse',
  },
  // TICKET-278 — natures de la catégorie « recherche ». Le libellé user-facing
  // de `decision` est « Jurisprudence » (vocabulaire avocat) ; la clé DB reste
  // `decision` (routing pipeline + `isDecisionCandidate` inchangés).
  decision: {
    key: 'decision',
    label: 'Jurisprudence',
    parentCategory: 'recherche',
  },
  doctrine: {
    key: 'doctrine',
    label: 'Doctrine',
    parentCategory: 'recherche',
  },
  analyse: {
    key: 'analyse',
    label: 'Analyse / Note',
    parentCategory: 'recherche',
  },
};

/**
 * Natures de document de la catégorie « recherche », dans l'ordre d'affichage
 * du sélecteur de dépôt (TICKET-278). Source unique pour les dialogs d'upload
 * et de sauvegarde agent.
 */
export const RECHERCHE_NATURES: readonly DocumentSubcategoryConfig[] = [
  DOCUMENT_SUBCATEGORIES.decision,
  DOCUMENT_SUBCATEGORIES.doctrine,
  DOCUMENT_SUBCATEGORIES.analyse,
] as const;

/**
 * Order used in dropdowns and selectors. Mirrors the sidebar tab order in
 * `DossierView` so users see the same sequence everywhere.
 */
// WHY plus d'ajout manuel : `correspondances` a rejoint `DOCUMENT_DESTINATION_ORDER`
// avec le connecteur courrier Microsoft. L'ajout à la main, écrit quand la liste
// partagée n'en portait que six, produit donc un DOUBLON silencieux — huit
// entrées dont deux identiques. Ici le seul consommateur est un `Set`, qui
// dédoublonne et masque le défaut ; le même ajout manuel dans le complément Word
// affichait, lui, deux lignes « Correspondances » avec des clés React en double.
// Une nouvelle destination s'ajoute dans `@hector/shared-types`, jamais ici.
export const DOCUMENT_CATEGORY_ORDER: readonly DocumentCategoryKey[] = [
  ...DOCUMENT_DESTINATION_ORDER,
];

const DOCUMENT_CATEGORY_KEY_SET = new Set<string>(DOCUMENT_CATEGORY_ORDER);
const DOCUMENT_SUBCATEGORY_KEY_SET = new Set<string>(Object.keys(DOCUMENT_SUBCATEGORIES));

export function isDocumentCategoryKey(value: unknown): value is DocumentCategoryKey {
  return typeof value === 'string' && DOCUMENT_CATEGORY_KEY_SET.has(value);
}

export function isDocumentSubcategoryKey(value: unknown): value is DocumentSubcategoryKey {
  return typeof value === 'string' && DOCUMENT_SUBCATEGORY_KEY_SET.has(value);
}

export function getCategoryConfig(key: string | null | undefined): DocumentCategoryConfig | null {
  if (!isDocumentCategoryKey(key)) return null;
  return DOCUMENT_CATEGORIES[key];
}

export function getCategoryLabel(key: string | null | undefined): string | null {
  return getCategoryConfig(key)?.label ?? null;
}

export function getSubcategoryConfig(
  key: string | null | undefined,
): DocumentSubcategoryConfig | null {
  if (!isDocumentSubcategoryKey(key)) return null;
  return DOCUMENT_SUBCATEGORIES[key];
}

export function getSubcategoryLabel(key: string | null | undefined): string | null {
  return getSubcategoryConfig(key)?.label ?? null;
}


/** Subcategories of a given category, in declaration order. */
export function getSubcategoriesFor(
  parent: DocumentCategoryKey,
): DocumentSubcategoryConfig[] {
  return Object.values(DOCUMENT_SUBCATEGORIES).filter((s) => s.parentCategory === parent);
}
